import { getElement, defineElement, PUB_SUB_EVENTS } from "./gw.js";
import "./parsed-element.js";
import { morph } from "./morph.js";
import { subscribe } from "./pubsub.js";
const SECTION_ID = "section-rendering-card-product";
const SECTION_ID_MANNEQUIN = "section-rendering-card-product-mannequin";
const SECTION_ID_SALE = "section-rendering-card-product-sale";
const SECTION_ID_SALE_MANNEQUIN =
  "section-rendering-card-product-sale-mannequin";
const SECTION_ID_NO_CAROUSEL = "section-rendering-card-product-nc";
const SECTION_ID_MANNEQUIN_NO_CAROUSEL =
  "section-rendering-card-product-mannequin-nc";
class CardProduct extends HTMLElement {
  constructor() {
    super();
    this.cache = /* @__PURE__ */ new Map();
    this.prefetchTimeouts = /* @__PURE__ */ new Map();
    this.variantChangeUnsubscriber = null;
    this.initialized = false;
    this.loadSliderImages = this.loadSliderImages.bind(this);
    this.setupSwatchHandlers();
    this.setupVariantChangeHandler();
  }

  connectedCallback() {
    this.init();
    this.addEventListener("pointerenter", this.loadSliderImages, {
      once: true,
    });
  }

  disconnectedCallback() {
    this.destroy();
  }

  loadSliderImages() {
    this.querySelectorAll("img").forEach((img) => {
      if (img.hasAttribute("data-srcset")) {
        img.setAttribute("srcset", img.getAttribute("data-srcset"));
        img.removeAttribute("data-srcset");
        img.setAttribute("src", img.getAttribute("data-src"));
        img.removeAttribute("data-src");
      }
    });
  }

  get variantSelector() {
    return this.querySelector("variant-selects");
  }

  get propertyInputsContainer() {
    return this.querySelector("div[data-property-inputs]");
  }

  updatePropertyInputs(variant) {
    const propertyInputsContainer = this.propertyInputsContainer;
    if (!propertyInputsContainer) return;
    let inputsMarkup = '';
    if (variant.preorder) {
      inputsMarkup += `
        <input type="hidden" name="properties[${window.variantStrings.saleType}]" value="${window.variantStrings.preorder}">
      `;

      if (variant.preorder_message) {
        inputsMarkup += `
          <input type="hidden" name="properties[_preorder_message]" value="${variant.preorder_message}">
        `;
      }
    }

    propertyInputsContainer.innerHTML = inputsMarkup;
  }

  async init() {
    this.options = {
      productHandle: this.getAttribute("product-handle") ?? null,
      mannequinImageFirst:
        this.hasAttribute("mannequin-image-first") &&
        this.getAttribute("mannequin-image-first") !== "false",
      enableProductImageCarousel:
        this.hasAttribute("product-image-carousel") &&
        this.getAttribute("product-image-carousel") !== "false",
      showDiscount:
        this.hasAttribute("show-discount") &&
        this.getAttribute("show-discount") !== "false",
    };
    if (this.options.productHandle && !this.initialized) {
      await this.renderProductCard();
      this.setupSwatchHandlers();
      this.setupVariantChangeHandler();
    }
    if (
      this.parentElement.getAttribute("data-gw-product-card-selector") === ""
    ) {
      this.parentElement.setAttribute(
        "data-gw-product-card-selector",
        this.productId,
      );
    }
    this.initialized = true;
  }

  get productId() {
    return this.dataset.id;
  }

  async renderProductCard() {
    const productHandle = this.options.productHandle;
    let section;
    if (this.options.showDiscount && this.options.enableProductImageCarousel) {
      section = this.options.mannequinImageFirst
        ? SECTION_ID_SALE_MANNEQUIN
        : SECTION_ID_SALE;
    } else if (this.options.enableProductImageCarousel) {
      section = this.options.mannequinImageFirst
        ? SECTION_ID_MANNEQUIN
        : SECTION_ID;
    } else {
      section = this.options.mannequinImageFirst
        ? SECTION_ID_MANNEQUIN_NO_CAROUSEL
        : SECTION_ID_NO_CAROUSEL;
    }
    const productUrl = `/products/${productHandle}?section_id=${section}`;
    const response = await fetch(productUrl, {
      priority: "low",
    });
    const responseText = await response.text();
    const parsedHTML = new DOMParser().parseFromString(
      responseText,
      "text/html",
    );
    const parsedCard = parsedHTML.querySelector("card-product");
    if (!parsedCard) {
      const removalTarget = this.closest(".product-grid")
        ? this.closest(".grid__item")
        : this.closest(".product-feed");
      removalTarget == null ? void 0 : removalTarget.remove();
      return;
    }
    this.innerHTML = parsedCard.innerHTML;
    this.setAttribute("data-id", parsedCard.dataset.id);
    this.dispatchCardLoadedEvent();
  }

  dispatchCardLoadedEvent() {
    const event = new CustomEvent("product-card:load", {
      detail: {
        product: this.options.productHandle,
      },
    });
    this.dispatchEvent(event);
  }

  setupSwatchHandlers() {
    const swatches = this.querySelectorAll(".swatch-button");
    if (swatches.length === 0) return;
    console.debug("CardProduct: Setting up swatch handlers", this);
    swatches.forEach((swatch) => {
      if (swatch._swatchHandlersInitialized) {
        return;
      }
      const clickHandler = this.handleSwatchClick.bind(this);
      const pointerEnterHandler = this.handleSwatchPointerEnter.bind(this);
      const pointerLeaveHandler = this.handleSwatchPointerLeave.bind(this);
      swatch.addEventListener("click", clickHandler);
      swatch.addEventListener("pointerenter", pointerEnterHandler);
      swatch.addEventListener("pointerleave", pointerLeaveHandler);
      swatch._swatchHandlersInitialized = true;
      swatch._swatchHandlers = {
        click: clickHandler,
        pointerenter: pointerEnterHandler,
        pointerleave: pointerLeaveHandler,
      };
    });
    this.updateCachedIndicators();
  }

  updateCachedIndicators() {
    const swatches = this.querySelectorAll(".product-swatch-button");
    swatches.forEach((swatch) => {
      const sectionUrl = this.getSectionUrl(swatch);
      if (this.cache.has(sectionUrl)) {
        swatch.classList.add("cached");
      } else {
        swatch.classList.remove("cached");
      }
    });
  }

  handleSwatchPointerEnter(event) {
    const swatch = event.currentTarget;
    if (!swatch.classList.contains("selected")) {
      const sectionUrl = this.getSectionUrl(swatch);
      this.clearPrefetchTimeout(sectionUrl);
      const timeoutId = setTimeout(() => {
        requestIdleCallback(() => {
          this.prefetchSwatchContent(swatch);
        });
      }, 100);
      this.prefetchTimeouts.set(sectionUrl, timeoutId);
      swatch.classList.add("prefetch-ready");
    }
  }

  handleSwatchPointerLeave(event) {
    const swatch = event.currentTarget;
    this.cancelPrefetch(swatch);
  }

  cancelPrefetch(swatch) {
    const sectionUrl = this.getSectionUrl(swatch);
    this.clearPrefetchTimeout(sectionUrl);
    swatch.classList.remove("prefetch-ready");
  }

  clearPrefetchTimeout(sectionUrl) {
    const timeoutId = this.prefetchTimeouts.get(sectionUrl);
    if (timeoutId) {
      console.debug("Clearing prefetch timeout for:", sectionUrl);
      clearTimeout(timeoutId);
      this.prefetchTimeouts.delete(sectionUrl);
    }
  }

  async prefetchSwatchContent(swatch) {
    const sectionUrl = this.getSectionUrl(swatch);
    if (this.cache.has(sectionUrl)) {
      swatch.classList.add("cached");
      swatch.classList.remove("prefetch-ready");
      return;
    }
    console.debug("Prefetching content for:", sectionUrl);
    try {
      const response = await fetch(sectionUrl, {
        priority: "low",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const htmlText = await response.text();
      this.cache.set(sectionUrl, htmlText);
      console.debug("Successfully prefetched:", sectionUrl);
      swatch.classList.add("cached");
    } catch (error) {
      console.error("Error prefetching:", error);
    } finally {
      swatch.classList.remove("prefetch-ready");
      this.prefetchTimeouts.delete(sectionUrl);
    }
  }

  async handleSwatchClick(event) {
    const swatch = event.currentTarget;
    const productUrl = swatch.dataset.url;
    if (
      swatch.classList.contains("is-selected") ||
      swatch.hasAttribute("aria-current")
    ) {
      return;
    }
    for (const sibling of swatch.parentElement.children) {
      sibling.classList.toggle("is-selected", sibling === swatch);
    }
    this.cancelPrefetch(swatch);
    swatch.classList.add("loading");
    try {
      const sectionUrl = this.getSectionUrl(swatch);
      let htmlText;
      if (this.cache.has(sectionUrl)) {
        console.debug("Using cached content for:", sectionUrl);
        htmlText = this.cache.get(sectionUrl);
      } else {
        console.debug("Fetching fresh content for:", sectionUrl);
        const response = await fetch(sectionUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        htmlText = await response.text();
        this.cache.set(sectionUrl, htmlText);
      }
      if (productUrl.startsWith("/collections")) {
        const urlParts = productUrl.match(
          /^(?<prefix>\/collections\/[^\/]+)(?<productPath>\/products\/.*)$/,
        );
        const urlTargetPattern = new RegExp(
          `(?<!${urlParts.groups.prefix.replaceAll("/", "\\/")})${urlParts.groups.productPath.replaceAll("/", "\\/")}`,
          "gm",
        );
        htmlText = htmlText.replace(urlTargetPattern, productUrl);
      }
      const html = new DOMParser().parseFromString(htmlText, "text/html");
      this.replaceCardContent(html);
      this.querySelector("product-form").init();
    } catch (error) {
      console.error("Error fetching product card:", error);
      window.location.href = productUrl;
    } finally {
      swatch.classList.remove("loading");
    }
  }

  replaceCardContent(html) {
    const newCardProductContent = html.querySelector(
      "[data-gw-product-card-selector] > card-product",
    );
    if (newCardProductContent) {
      const oldCardSwatches = this.querySelector("[data-product-swatches]");
      const newCardSwatches = newCardProductContent.querySelector(
        "[data-product-swatches]",
      );
      newCardSwatches.replaceWith(oldCardSwatches);
      morph(this, newCardProductContent);
      setTimeout(() => {
        this.updateCachedIndicators();
        this.resetSliderComponent();
        this.setupVariantChangeHandler();
        const CONTAINER_SELECTOR = `card-product[data-id="${this.productId}"]`;
        this.dispatchEvent(
          new CustomEvent("product-card:change", {
            bubbles: true,
            detail: {
              card: this,
              containerSelector: CONTAINER_SELECTOR,
            },
          }),
        );
        this.loadSliderImages();
      });
    }
  }

  setupVariantChangeHandler() {
    const productData = this.getProductData();
    if (!productData) return;
    console.debug("CardProduct: Setting up variant change handler", this);
    this.variantChangeUnsubscriber = subscribe(
      `${PUB_SUB_EVENTS.optionValueSelectionChange}:${productData.id}`,
      this.handleVariantChange.bind(this),
    );
  }

  getProductData() {
    const productScript = this.querySelector("script[data-product-info]");
    if (!productScript) return null;
    try {
      return JSON.parse(productScript.textContent);
    } catch (error) {
      console.error("Error parsing product data:", error);
      return null;
    }
  }

  handleVariantChange(event) {
    const { value } = event.data;
    const productData = this.getProductData();
    if (!productData || !value) return;
    const matchingVariant = this.findVariantByOptionValues(productData, value);
    this.updatePropertyInputs(matchingVariant);
    this.updateSubmitButton(matchingVariant);
  }

  findVariantByOptionValues(productData, value) {
    return productData.variants.find((variant) => {
      return variant.option1 == value;
    });
  }

  updateSubmitButton(variant) {
    var _a, _b;
    const submitButton = this.querySelector('[type="submit"]');
    const submitButtonText =
      submitButton == null ? void 0 : submitButton.querySelector("span");
    const soldOutMessage =
      submitButton == null
        ? void 0
        : submitButton.querySelector(".sold-out-message");
    if (!submitButton || !submitButtonText) return;
    if (!variant || !variant.available) {
      submitButton.setAttribute("disabled", "disabled");
      submitButtonText.classList.add("hidden");
      if (soldOutMessage) {
        soldOutMessage.classList.remove("hidden");
      } else {
        submitButtonText.textContent =
          ((_a = window.variantStrings) == null ? void 0 : _a.soldOut) ||
          "Sold out";
        submitButtonText.classList.remove("hidden");
      }
    } else {
      submitButton.removeAttribute("disabled");
      submitButtonText.classList.remove("hidden");
      if (soldOutMessage) {
        soldOutMessage.classList.add("hidden");
      }
      submitButtonText.textContent =
        ((_b = window.variantStrings) == null ? void 0 : _b.addToCart) ||
        "Add to cart";
      const variantIdInput = this.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }
    }
  }

  destroy() {
    this.cache.clear();
    this.prefetchTimeouts.forEach((timeoutId, url) => {
      console.debug("Clearing prefetch timeout on destroy:", url);
      clearTimeout(timeoutId);
    });
    this.prefetchTimeouts.clear();
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    const swatches = this.querySelectorAll(".product-swatch-button");
    swatches.forEach((link) => {
      if (link._swatchHandlers) {
        link.removeEventListener("click", link._swatchHandlers.click);
        link.removeEventListener(
          "pointerenter",
          link._swatchHandlers.pointerenter,
        );
        link.removeEventListener(
          "pointerleave",
          link._swatchHandlers.pointerleave,
        );
        delete link._swatchHandlers;
        delete link._swatchHandlersInitialized;
      }
    });
  }

  getSectionUrl(swatch) {
    const productUrl = swatch.dataset.url;
    let section;
    if (this.options.showDiscount && this.options.enableProductImageCarousel) {
      section = this.options.mannequinImageFirst
        ? SECTION_ID_SALE_MANNEQUIN
        : SECTION_ID_SALE;
    } else if (this.options.enableProductImageCarousel) {
      section = this.options.mannequinImageFirst
        ? SECTION_ID_MANNEQUIN
        : SECTION_ID;
    } else {
      section = this.options.mannequinImageFirst
        ? SECTION_ID_MANNEQUIN_NO_CAROUSEL
        : SECTION_ID_NO_CAROUSEL;
    }
    const sectionUrl = `${productUrl}?section_id=${section}`;
    return sectionUrl;
  }

  resetSliderComponent() {
    const sliderComponent = this.querySelector("slider-component");
    if (sliderComponent) {
      sliderComponent.resetPages();
      setTimeout(() => {
        sliderComponent.playActiveSlideVideo();
      }, 100);
    }
  }
}
if (!getElement("card-product")) {
  defineElement("card-product", CardProduct);
}
